import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuthStore } from './stores/authStore';
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';
import LoadingScreen from './components/ui/LoadingScreen';

const isNativePlatform = Capacitor.isNativePlatform();

// Lazy loaded pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const TasksPage = lazy(() => import('./pages/tasks/TasksPage'));
const TaskDetailsPage = lazy(() => import('./pages/tasks/TaskDetailsPage'));
const CreateTaskPage = lazy(() => import('./pages/tasks/CreateTaskPage'));
const EditTaskPage = lazy(() => import('./pages/tasks/EditTaskPage'));
const ImportTasksPage = lazy(() => import('./pages/tasks/ImportTasksPage'));
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage'));
const EmployeesPage = lazy(() => import('./pages/employees/EmployeesPage'));
const ClientsPage = lazy(() => import('./pages/clients/ClientsPage'));
const ClientDetailsPage = lazy(() => import('./pages/clients/ClientDetailsPage'));
const ImportClientsPage = lazy(() => import('./pages/clients/ImportClientsPage'));
const LiftsAdminPage = lazy(() => import('./pages/lifts/LiftsAdminPage'));
const LiftDetailsPage = lazy(() => import('./pages/lifts/LiftDetailsPage'));
const RealTimeMapPage = lazy(() => import('./pages/map/RealTimeMapPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const MasterControlPanel = lazy(() => import('./pages/master/MasterControlPanel'));
const FilesGalleryPage = lazy(() => import('./pages/files/FilesGalleryPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const MobileWorkerView = lazy(() => import('./pages/worker/MobileWorkerView'));
const CreateOnDutyTaskPage = lazy(() => import('./pages/worker/CreateOnDutyTaskPage'));
const MobileAdminView = lazy(() => import('./pages/admin/MobileAdminView'));
const MarketingLanding = lazy(() => import('./pages/marketing/MarketingLanding'));
const ElevatorLanding = lazy(() => import('./pages/marketing/ElevatorLanding'));
const Website = lazy(() => import('./pages/marketing/Website'));

function App() {
  const { isAuthenticated, user, checkAuth, hasPermission, hasFeature } = useAuthStore();

  // Check authentication on app start
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Na native platformi uvek ide na odgovarajuci mobile view
  const getMobileRoute = () => {
    if (!user) return '/login';
    return (user.role === 'admin' || user.role === 'master') ? '/admin/mobile' : '/worker/mobile';
  };

  // Route protection based on permissions
  const requirePermission = (permission: string) => (element: JSX.Element) => {
    if (isNativePlatform) return <Navigate to={getMobileRoute()} replace />;
    return user && hasPermission(permission) ? element : <Navigate to="/dashboard" replace />;
  };

  // Route protection based on roles
  const requireRole = (requiredRole: string) => (element: JSX.Element) => {
    if (isNativePlatform) return <Navigate to={getMobileRoute()} replace />;
    return user && user.role === requiredRole ? element : <Navigate to="/dashboard" replace />;
  };

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public marketing landing on root; authenticated users go to dashboard or mobile view */}
        <Route 
          path="/" 
          element={
            isAuthenticated 
              ? <Navigate to={isNativePlatform ? getMobileRoute() : '/dashboard'} replace /> 
              : (isNativePlatform ? <Navigate to="/login" replace /> : <MarketingLanding />)
          } 
        />

        {/* Additional marketing/landing pages */}
        <Route path="/elevator" element={<ElevatorLanding />} />
        <Route path="/website" element={<Website />} />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route 
            path="/login" 
            element={
              !isAuthenticated || user?.role === 'radnik' 
                ? <LoginPage /> 
                : <Navigate to="/dashboard" replace />
            } 
          />
        </Route>

        {/* Worker Mobile View - Without Dashboard Layout */}
        <Route 
          path="/worker/mobile" 
          element={isAuthenticated ? <MobileWorkerView /> : <Navigate to="/login" replace />} 
        />

        {/* Worker Create On-Duty Task - Separate page */}
        <Route 
          path="/worker/create-on-duty-task" 
          element={isAuthenticated ? <CreateOnDutyTaskPage /> : <Navigate to="/login" replace />} 
        />

        {/* Admin Mobile View - Without Dashboard Layout, only for admin role */}
        <Route 
          path="/admin/mobile" 
          element={isAuthenticated && (user?.role === 'admin' || user?.role === 'master') ? <MobileAdminView /> : <Navigate to="/login" replace />} 
        />

        {/* Protected routes */}
        <Route 
          element={
            isAuthenticated ? <DashboardLayout /> : <Navigate to="/login" replace />
          }
        >
          <Route 
            path="/dashboard" 
            element={isNativePlatform ? <Navigate to={getMobileRoute()} replace /> : <DashboardPage />} 
          />
          
          {/* Master Control Panel - Only for master role */}
          <Route path="/master" element={requireRole('master')(<MasterControlPanel />)} />
          
          {/* Tasks routes - accessible to all authenticated users */}
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/tasks/new" element={requirePermission('create_task')(<CreateTaskPage />)} />
          <Route path="/tasks/import" element={requirePermission('create_task')(<ImportTasksPage />)} />
          <Route path="/tasks/:taskId" element={<TaskDetailsPage />} />
          <Route path="/tasks/:taskId/edit" element={requirePermission('edit_tasks')(<EditTaskPage />)} />
          
          {/* Calendar route */}
          <Route path="/calendar" element={<CalendarPage />} />
          
          {/* Employees routes */}
          <Route path="/employees" element={requirePermission('view_employees')(<EmployeesPage />)} />
          
          {/* Clients routes */}
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/clients/import" element={<ImportClientsPage />} />
          <Route path="/clients/:id" element={<ClientDetailsPage />} />
          
          {/* Lifts routes */}
          <Route path="/lifts" element={<LiftsAdminPage />} />
          <Route path="/lifts/:id" element={<LiftDetailsPage />} />
          
          {/* Map route */}
          <Route path="/map" element={<RealTimeMapPage />} />
          <Route path="/gallery" element={requirePermission('manage_employees')(<FilesGalleryPage />)} />
          
          <Route path="/profile" element={<ProfilePage />} />
        </Route>

        {/* 404 Route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;